import { getApiText } from '../../../shared/i18n';
import type { MenuBuilder, MenuItemData } from './types';

export type MenuFlowSection = {
  title: string;
  data: (MenuItemData & { _rowKey?: string })[];
  foodTypeId: number;
  selectedQuantity: number;
  typeLimit: number;
  displayLimit: number;
  isMealSection: boolean;
};

export type MenuFlowSectionRange = {
  startIndex: number;
  endIndex: number;
  title: string;
  foodTypeId: number;
  selectedQuantity: number;
  displayLimit: number;
};

export type MenuFlowFlatItem = {
  item: MenuItemData & { _rowKey?: string };
  sectionIndex: number;
  section: MenuFlowSection;
  rowKey: string;
};

export type MenuFlowFlatRow =
  | { type: 'item'; item: MenuFlowFlatItem }
  | { type: 'divider'; sectionIndex: number; rowKey: string }
  | { type: 'section_anchor'; sectionIndex: number; section: MenuFlowSection; rowKey: string };

/** Daily menu flow aliases — same list model as MenuFlow. */
export type DailyMenuSection = MenuFlowSection;
export type DailyMenuSectionRange = MenuFlowSectionRange;
export type DailyMenuFlatItem = MenuFlowFlatItem;
export type DailyMenuFlatRow = MenuFlowFlatRow;

export type MenuFlowTabListModel = {
  sections: MenuFlowSection[];
  flatRows: MenuFlowFlatRow[];
  sectionRanges: MenuFlowSectionRange[];
  sectionOffsets: number[];
  rowOffsets: number[];
};

export type MenuFlowTabbedListCache = {
  tabs: string[];
  models: Record<string, MenuFlowTabListModel>;
};

type BuildMenuFlowSectionsArgs = {
  builders: MenuBuilder[];
  mealsCount: number;
  snacksCount: number;
  selectionLimit?: Record<number, number> | unknown[] | null;
};

/**
 * List row shows the selected replaceable when qty > 0; `_rowKey` stays on the parent slot.
 */
export function resolveMenuFlowRowDisplayItem(
  it: MenuItemData & { replaceable_menu_items?: MenuItemData[] },
  builder_food_type_id: number,
  idx: number,
): MenuItemData & { _rowKey: string } {
  const parent_id = it.id;
  const repls = ((it.replaceable_menu_items ?? []) as MenuItemData[]).filter(r => (r.quantity ?? 0) > 0);
  const row_key = `${builder_food_type_id}-${parent_id}-${idx}`;
  if (repls.length === 0) {
    return { ...it, _rowKey: row_key };
  }
  const selected_repl =
    repls.length === 1
      ? repls[0]!
      : repls.reduce((best, r) => {
          const sn = Number((r as { selection_number?: number }).selection_number ?? 0);
          const bsn = Number((best as { selection_number?: number }).selection_number ?? 0);
          return sn >= bsn ? r : best;
        });
  return { ...selected_repl, _rowKey: row_key };
}

function getIsMealSection(builder: MenuBuilder): boolean {
  const cat = (builder.type_data?.[0]?.food_type_category ?? '').toString().toLowerCase();
  return cat === 'meal';
}

function getSectionLimit(
  builder: MenuBuilder,
  meals_count: number,
  snacks_count: number,
  selection_limit?: Record<number, number> | unknown[] | null,
): number {
  const is_meal = getIsMealSection(builder);
  const daily_cap = is_meal ? meals_count : snacks_count;
  const map = selection_limit && typeof selection_limit === 'object' && !Array.isArray(selection_limit)
    ? (selection_limit as Record<number, number>)
    : null;
  const raw = map && typeof map[builder.food_type_id] === 'number' ? map[builder.food_type_id]! : null;
  return raw != null && raw > 0 ? Math.min(raw, daily_cap) : daily_cap;
}

export function buildMenuFlowSections({
  builders,
  mealsCount,
  snacksCount,
  selectionLimit,
}: BuildMenuFlowSectionsArgs): MenuFlowSection[] {
  if (builders.length === 0) {
    return [];
  }

  const total_meals_selected = builders.filter(b => getIsMealSection(b)).reduce((s, b) => s + (b.selected_quantity ?? 0), 0);
  const total_snacks_selected = builders.filter(b => !getIsMealSection(b)).reduce((s, b) => s + (b.selected_quantity ?? 0), 0);

  return builders.map(builder => {
    const is_meal = getIsMealSection(builder);
    const type_limit = getSectionLimit(builder, mealsCount, snacksCount, selectionLimit);
    const selected_qty = builder.selected_quantity ?? 0;
    const daily_remaining = is_meal ? mealsCount - total_meals_selected : snacksCount - total_snacks_selected;
    const display_limit = Math.min(type_limit, Math.max(0, daily_remaining) + selected_qty);
    const data = (builder.type_data ?? []).map((it, idx) =>
      resolveMenuFlowRowDisplayItem(it as MenuItemData & { replaceable_menu_items?: MenuItemData[] }, builder.food_type_id, idx),
    );

    return {
      title: getApiText(builder as unknown as Record<string, unknown>, 'type_name', 'a_type_name') || builder.type_name,
      data,
      foodTypeId: builder.food_type_id,
      selectedQuantity: selected_qty,
      typeLimit: type_limit,
      displayLimit: display_limit,
      isMealSection: is_meal,
    };
  });
}

export function buildFlatListFromSections(
  sections: MenuFlowSection[],
  section_row_mode: 'divider' | 'header' = 'divider',
): {
  flatRows: MenuFlowFlatRow[];
  sectionRanges: MenuFlowSectionRange[];
} {
  const flat_rows: MenuFlowFlatRow[] = [];
  const section_ranges: MenuFlowSectionRange[] = [];

  for (let s = 0; s < sections.length; s++) {
    const section = sections[s];
    const rows = section.data ?? [];
    const start_index = flat_rows.length;
    const insert_section_header = section_row_mode === 'header' && s > 0;

    if (insert_section_header) {
      flat_rows.push({
        type: 'section_anchor',
        sectionIndex: s,
        section,
        rowKey: `section-${s}-${section.foodTypeId ?? 'na'}`,
      });
    }

    const items_start = flat_rows.length;
    for (let idx = 0; idx < rows.length; idx++) {
      const it = rows[idx];
      flat_rows.push({
        type: 'item',
        item: {
          item: it,
          sectionIndex: s,
          section,
          rowKey: it._rowKey ?? `${section.foodTypeId}-${it.id}-${idx}`,
        },
      });
    }

    const end_index = flat_rows.length === start_index ? start_index : flat_rows.length - 1;
    section_ranges.push({
      // Header rows mark food-type transitions (skip first — sticky chip already shows it).
      startIndex: insert_section_header ? start_index : items_start,
      endIndex: end_index,
      title: section.title,
      foodTypeId: section.foodTypeId,
      selectedQuantity: section.selectedQuantity,
      displayLimit: section.displayLimit,
    });

    if (section_row_mode === 'divider') {
      flat_rows.push({
        type: 'divider',
        sectionIndex: s,
        rowKey: `divider-${s}-${section.foodTypeId ?? 'na'}`,
      });
    }
  }

  if (flat_rows.length > 0 && flat_rows[flat_rows.length - 1].type === 'divider') {
    flat_rows.pop();
  }

  return { flatRows: flat_rows, sectionRanges: section_ranges };
}

function buildOffsets(
  flat_rows: MenuFlowFlatRow[],
  section_ranges: MenuFlowSectionRange[],
  full_header_height: number,
  item_row_height: number,
  section_gap: number,
): { sectionOffsets: number[]; rowOffsets: number[] } {
  const row_offsets: number[] = [];
  let acc = full_header_height;
  flat_rows.forEach((row, i) => {
    row_offsets[i] = acc;
    acc += row.type === 'item' ? item_row_height : section_gap;
  });
  const section_offsets = section_ranges.map(r => row_offsets[r.startIndex] ?? full_header_height);
  return { sectionOffsets: section_offsets, rowOffsets: row_offsets };
}

function buildTabModel(
  sections: MenuFlowSection[],
  full_header_height: number,
  item_row_height: number,
  section_gap: number,
  section_row_mode: 'divider' | 'header' = 'divider',
): MenuFlowTabListModel {
  const { flatRows, sectionRanges } = buildFlatListFromSections(sections, section_row_mode);
  const { sectionOffsets, rowOffsets } = buildOffsets(flatRows, sectionRanges, full_header_height, item_row_height, section_gap);
  return { sections, flatRows, sectionRanges, sectionOffsets, rowOffsets };
}

/** Precompute flat list models for All + each food type so tab switches are O(1). */
export function buildMenuFlowTabbedListCache(
  builders: MenuBuilder[],
  args: {
    mealsCount: number;
    snacksCount: number;
    selectionLimit?: Record<number, number> | unknown[] | null;
    fullHeaderHeight: number;
    itemRowHeight: number;
    sectionGap: number;
    sectionRowMode?: 'divider' | 'header';
  },
): MenuFlowTabbedListCache {
  const section_row_mode = args.sectionRowMode ?? 'divider';
  const all_sections = buildMenuFlowSections({
    builders,
    mealsCount: args.mealsCount,
    snacksCount: args.snacksCount,
    selectionLimit: args.selectionLimit,
  });

  const type_names = Array.from(new Set(builders.map(b => b.type_name).filter(Boolean)));
  const tabs = ['All', ...type_names];
  const models: Record<string, MenuFlowTabListModel> = {
    All: buildTabModel(all_sections, args.fullHeaderHeight, args.itemRowHeight, args.sectionGap, section_row_mode),
  };

  for (const type_name of type_names) {
    const filtered_builders = builders.filter(b => b.type_name === type_name);
    const sections = buildMenuFlowSections({
      builders: filtered_builders,
      mealsCount: args.mealsCount,
      snacksCount: args.snacksCount,
      selectionLimit: args.selectionLimit,
    });
    models[type_name] = buildTabModel(sections, args.fullHeaderHeight, args.itemRowHeight, args.sectionGap, section_row_mode);
  }

  return { tabs, models };
}
